import { Slot, CreateSlotDTO }             from '../../Model/Slot';
import { SlotRepository }                  from '../Repositories/SlotRepository';
import { User }                            from '../../../User/Model/User';
import { assertAdminOrManager } from './Utils/Permission';
import { calcRemaining, minStartDateYmdForLimitedPartnerCreate } from './Utils/DateUtil';
import { isSlotOperationRestricted, getRestrictionMessage } from './Utils/TimeRestriction';


export class CreateSlotUseCase {
  constructor(private readonly repo: SlotRepository) {}

  /**
   * 새 슬롯 생성
   *  - ADMIN 권한만 가능
   *  - 필수값 검증 → 서버 호출
   *  - remaining_days 계산하여 반환
   */
  async execute(dto: Partial<CreateSlotDTO>, current: User): Promise<Slot> {
    /* ── 시간 제한 체크 ─────────────────────────── */
    if (isSlotOperationRestricted(current, '발주')) {
      throw new Error(getRestrictionMessage('생성', current));
    }

    /* ── 권한 체크 ─────────────────────────────── */
     assertAdminOrManager(current);

    /* ── 필수 값 유효성 ────────────────────────── */
    const required: (keyof CreateSlotDTO)[] = [
      'sellerId',
      'slot_number',
      'start_date',
      'end_date',
    ];

    const missing = required.find(k => dto[k] == null);
    if (missing) {
      throw new Error(`필수 값(${missing})이 누락되었습니다.`);
    }

    /* ── DTO 구성 (creator, status 기본값 포함) ── */
    const payload: CreateSlotDTO = {
      sellerId     : dto.sellerId!,
      manager      : dto.manager ?? null,
      slot_number  : dto.slot_number!,
      main_keyword : dto.main_keyword!,
      word_keyword : dto.word_keyword ?? '-',
      mid_single   : dto.mid_single    ?? '',
      mid_compare  : dto.mid_compare   ?? '',
      product_url  : dto.product_url   ?? '',
      compare_url  : dto.compare_url   ?? '',
      memo         : dto.memo,
      start_date   : dto.start_date!,
      end_date     : dto.end_date!,
      status       : dto.status ?? '정상'
    };

    const isLimitedAdmin =
      current.grade === 'ADMIN' && Boolean(current.is_limited_partner_admin);
    if (isLimitedAdmin) {
      const minStart = minStartDateYmdForLimitedPartnerCreate();
      if (payload.start_date < minStart) {
        throw new Error(
          `제한형 관리자는 시작일을 ${minStart} 이후(내일)부터만 지정할 수 있습니다.`,
        );
      }
    }

    /* ── 저장 & 후처리 ─────────────────────────── */
    const saved = await this.repo.createSlot({ ...payload });
    return { ...saved, remaining_days: calcRemaining(saved.end_date) };
  }
}
